import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Content-shaped loading placeholders for the `/billing` cards — same contract as
 * `settings/section-skeletons`, for the shapes settings has none of (usage tiles,
 * plan band, pricing grid, proration, notice). Cards that render real `SectionRow`s reuse the
 * settings primitives instead; settings never imports from here.
 */

/**
 * The "Usage" heading row: title on the left, "Compare plans" on the right.
 * Same height and bottom margin as `BillingSection`'s real heading row.
 */
export function UsageHeadingSkeleton() {
  return (
    <div aria-hidden="true" className="mb-2.5 flex h-[26px] items-center justify-between gap-3">
      <Skeleton className="h-4 w-[60px] rounded" />
      <Skeleton className="h-4 w-[110px] rounded" />
    </div>
  );
}

/**
 * The two usage tiles: label + pill, the big figure, the bar, a footer line.
 * Shapes `UsageCard` on the same auto-fit grid, so the tiles don't reflow
 * when the summary lands.
 */
export function UsageTilesSkeleton({ tiles = 2 }: { tiles?: number }) {
  return (
    <div aria-hidden="true" className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,260px),1fr))] gap-3.5">
      {Array.from({ length: tiles }, (_, i) => (
        <div
          key={i}
          data-testid="usage-tile-skeleton"
          className="flex flex-col gap-3 rounded-[var(--r-lg)] border border-[color:var(--ink-200)] bg-[color:var(--paper)] px-[18px] pb-[17px] pt-4"
        >
          <div className="flex items-center justify-between gap-2">
            <Skeleton className="h-3.5 w-[140px] max-w-full rounded" />
            <Skeleton className="h-5 w-[96px] shrink-0 rounded-full" />
          </div>
          <Skeleton className="h-[30px] w-[150px] max-w-full rounded" />
          <Skeleton className="h-2 w-full rounded-full" />
          <Skeleton className="h-3 w-[200px] max-w-full rounded" />
        </div>
      ))}
    </div>
  );
}

/**
 * Receipt rows: outcome marker, date over its reference, amount over its
 * status. Shapes `InvoiceHistoryCard`'s list, hairline between rows as it draws.
 */
export function InvoiceRowsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          data-testid="invoice-row-skeleton"
          className={cn(
            'flex items-center gap-3.5 px-4 py-3.5 sm:px-[18px]',
            i > 0 && 'border-t border-[color:var(--ink-100)]',
          )}
        >
          <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-4 w-[110px] rounded" />
            <Skeleton className="h-3 w-[180px] max-w-full rounded" />
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <Skeleton className="h-4 w-[96px] rounded" />
            <Skeleton className="h-5 w-[56px] rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The plan band: eyebrow, plan name + chips, price, and the facts box beside
 * it. Shapes `SubscriptionDetailsCard`. Neutral-bordered (no brand tint) —
 * whether the org is on a paid plan is exactly what isn't known yet.
 */
export function PlanBandSkeleton() {
  return (
    <div
      aria-hidden="true"
      data-testid="plan-band-skeleton"
      className="flex flex-wrap items-start gap-x-[26px] gap-y-[18px] rounded-[var(--r-xl)] border border-[color:var(--ink-200)] bg-[color:var(--paper)] px-4 py-[18px] sm:px-6 sm:py-[22px]"
    >
      <div className="flex min-w-0 flex-[999_1_260px] flex-col gap-2.5">
        <Skeleton className="h-3 w-[96px] rounded" />
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-[30px] w-[150px] max-w-full rounded" />
          <Skeleton className="h-5 w-[60px] shrink-0 rounded-full" />
        </div>
        <Skeleton className="h-5 w-[170px] max-w-full rounded" />
      </div>
      <Skeleton className="h-[74px] flex-[1_1_300px] rounded-[var(--r-lg)]" />
    </div>
  );
}

/**
 * Proration breakdown: label/value pairs, the last set off by its own hairline
 * like `ProrationSummary`'s total. Shapes `PlanUpgradeDialog`'s confirm body;
 * three rows by default (plan amount, tax, charged now).
 */
export function ProrationRowsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div aria-hidden="true" className="flex flex-col gap-2">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          data-testid="proration-row-skeleton"
          className={cn(
            'flex items-center justify-between',
            i === rows - 1 && 'border-t border-[color:var(--ink-100)] pt-2',
          )}
        >
          <Skeleton className="h-3.5 w-[120px] rounded" />
          <Skeleton className="h-3.5 w-[72px] shrink-0 rounded" />
        </div>
      ))}
    </div>
  );
}

/**
 * The pricing surface: centred header over the tier grid. Shapes
 * `UpgradePlansCard`; four cards on the same auto-fit grid the real one
 * uses, so the column count breaks at the same widths.
 */
export function PlanTierGridSkeleton({ cards = 4 }: { cards?: number }) {
  return (
    <div aria-hidden="true" className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="h-8 w-[320px] max-w-full rounded" />
        <Skeleton className="h-5 w-[240px] max-w-full rounded" />
        <Skeleton className="mt-2 h-10 w-[220px] max-w-full rounded-full" />
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,260px),1fr))] gap-4">
        {Array.from({ length: cards }, (_, i) => (
          <PlanTierCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/**
 * One tier card: icon + name, price, CTA, feature checklist. Exported for a
 * single-card surface; five feature lines matches most tiers.
 */
export function PlanTierCardSkeleton({ features = 5 }: { features?: number }) {
  return (
    <div
      aria-hidden="true"
      data-testid="plan-tier-card-skeleton"
      className="flex flex-col gap-4 rounded-lg border border-[color:var(--ink-200)] bg-[color:var(--paper)] p-5"
    >
      <div className="flex items-center gap-2">
        <Skeleton className="h-5 w-5 shrink-0 rounded" />
        <Skeleton className="h-4 w-[90px] rounded" />
      </div>
      <div className="flex items-baseline gap-1.5">
        <Skeleton className="h-7 w-[86px] rounded" />
        <Skeleton className="h-3.5 w-[52px] rounded" />
      </div>
      {/* The CTA is `w-full` on every branch of `PlanTierCardCta`. */}
      <Skeleton className="h-10 w-full rounded-md" />
      <div className="flex flex-col gap-2">
        {Array.from({ length: features }, (_, i) => (
          <div key={i} data-testid="plan-feature-skeleton" className="flex items-start gap-2">
            <Skeleton className="h-4 w-4 shrink-0 rounded-full" />
            <Skeleton className="h-4 w-full rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * An `AlertBanner`-shaped box. Shapes `UpgradePromptDialog`, whose every
 * resolved branch is a banner. Neutral-bordered on purpose: which variant
 * arrives (info or error) is exactly what isn't known yet.
 */
export function NoticeBannerSkeleton({ lines = 2 }: { lines?: number }) {
  return (
    <div
      aria-hidden="true"
      data-testid="notice-banner-skeleton"
      className="flex flex-col gap-1.5 rounded-md border border-[color:var(--ink-200)] px-3 py-2"
    >
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          className={cn('h-4 rounded', i === lines - 1 ? 'w-2/3' : 'w-full')}
        />
      ))}
    </div>
  );
}
